/**
 * Bond Mentor — Signal Coverage ("Data Truth" honesty layer)
 *
 * Classifies each of the eight signal components by how real its data is right
 * now, and rolls that up into a single weighted "percent live" figure for the
 * master light. This is what powers the Live / Estimated / Backup / Placeholder
 * badges so users always know how much of today's reading is genuinely live.
 *
 * Coverage classes:
 *   live        — genuine, fresh, real-market data
 *   estimated   — real but derived/estimated (e.g. MOVE estimated from VIX)
 *   backup      — verified backup snapshot or safety baseline (real but not fresh)
 *   placeholder — a fixed stand-in value, not yet connected to a real source
 *   demo        — scenario / training data (the app is in Demo mode)
 */

// Components that are still fixed stand-ins until Drop A ("Data Truth") wires
// them to real sources. Keep this list in sync with server signalComputer.js.
export const PLACEHOLDER_COMPONENTS = ['vigilanteActivity', 'moneyFlows', 'globalBonds'];

// Maps each component to the data-freshness feed key(s) the server reports.
const FRESHNESS_KEY = {
  yieldCurve: ['yieldCurve'],
  creditSpreads: ['creditSpreads'],
  fedPosture: ['fed'],
  volatility: ['vix', 'move'],
  liquidity: ['fed'],
};

export const COVERAGE_META = {
  live: { label: 'Live', color: '#22c55e', tip: 'Genuine, fresh real-market data.' },
  estimated: { label: 'Estimated', color: '#eab308', tip: 'Real but derived or estimated, not an official feed.' },
  backup: { label: 'Backup', color: '#38bdf8', tip: 'Verified backup snapshot or safety baseline — real but not live.' },
  placeholder: { label: 'Placeholder', color: '#94a3b8', tip: 'A fixed stand-in value. Not connected to a real source yet.' },
  demo: { label: 'Demo', color: '#64748b', tip: 'Scenario / training data. The app is not in live mode.' },
};

function weightToNumber(weight) {
  const n = parseFloat(String(weight).replace('%', ''));
  return Number.isFinite(n) ? n : 0;
}

function classifyFromFreshness(feeds) {
  // feeds: array of freshness items {status, source, note} for this component.
  const text = feeds
    .map((f) => `${f?.status || ''} ${f?.source || ''} ${f?.note || ''}`)
    .join(' ')
    .toLowerCase();

  if (!text.trim()) return 'live'; // connected and unflagged — treat as live
  if (text.includes('verified backup') || text.includes('last clean')) return 'backup';
  if (text.includes('safety baseline') || text.includes('configured')) return 'backup';
  if (text.includes('estimate')) return 'estimated';
  if (text.includes('unavailable') || text.includes('missing')) return 'placeholder';
  return 'live';
}

/**
 * @param {object} meta       SUB_SIGNAL_META (component -> {label, weight, ...})
 * @param {object|null} liveData full /api/all payload (null in demo)
 * @param {boolean} isLive    whether the app is in live mode
 */
export function computeSignalCoverage(meta, liveData = null, isLive = false) {
  const freshness = Array.isArray(liveData?.dataFreshness) ? liveData.dataFreshness : [];
  const freshnessByKey = freshness.reduce((acc, item) => {
    if (item?.key) (acc[item.key] = acc[item.key] || []).push(item);
    return acc;
  }, {});

  // The server now reports a per-component source map (Drop A). Prefer it when
  // present so badges reflect exactly what the engine actually scored.
  const serverSources = liveData?.signal?.subSignalSources || null;

  const components = Object.entries(meta).map(([key, info]) => {
    const weight = weightToNumber(info.weight);
    let coverage;

    if (!isLive) {
      coverage = 'demo';
    } else if (serverSources && serverSources[key]) {
      const reported = serverSources[key];
      if (reported === 'live') {
        // Still honor a verified-backup state from the freshness feed.
        const feedKeys = FRESHNESS_KEY[key] || [];
        const feeds = feedKeys.flatMap((fk) => freshnessByKey[fk] || []);
        coverage = classifyFromFreshness(feeds) === 'backup' ? 'backup' : 'live';
      } else if (reported === 'estimated') {
        coverage = 'estimated';
      } else if (reported === 'backup') {
        coverage = 'backup';
      } else {
        coverage = 'placeholder';
      }
    } else if (PLACEHOLDER_COMPONENTS.includes(key)) {
      coverage = 'placeholder';
    } else {
      const feedKeys = FRESHNESS_KEY[key] || [];
      const feeds = feedKeys.flatMap((fk) => freshnessByKey[fk] || []);
      coverage = classifyFromFreshness(feeds);
    }

    return { key, label: info.label, weight, coverage };
  });

  const counts = { live: 0, estimated: 0, backup: 0, placeholder: 0, demo: 0 };
  let liveWeight = 0;
  components.forEach((c) => {
    counts[c.coverage] += 1;
    if (c.coverage === 'live') liveWeight += c.weight;
    else if (c.coverage === 'estimated' || c.coverage === 'backup') liveWeight += c.weight * 0.5;
  });

  const livePercent = Math.round(liveWeight);

  return {
    mode: isLive ? 'live' : 'demo',
    components,
    counts,
    livePercent,
    // A short human summary used by the master-light badge.
    summary: isLive
      ? `${livePercent}% live`
      : 'Demo data',
  };
}

export default computeSignalCoverage;
